import 'intersection-observer'; // Vuetify v-lazy uses the w3c/IntersectionObserver under the hood. Need polyfill for more browser support.
import 'img-comparison-slider'; // Import the img-comparison-slider custom element
import 'img-comparison-slider/dist/styles.css'; // Import the img-comparison-slider styles

import Vue from 'vue';
import App from './App.vue';
import router from './router';
import store from './store';
import vuetify from './plugins/vuetify';
import AutoSoundMachine from './plugins/AutoSoundMachine';
import VueMoment from 'vue-moment';
import VueScrollTo from 'vue-scrollto';
import './filters';
import './styles/global.css';
import './styles/vuetify-overrides.css';
import _get from 'lodash/get';
import BaseButton from '@/components/base/BaseButton.vue';
import { RawLocation, Route } from 'vue-router/types/router';
import VueRouter from 'vue-router';
import { ROUTER } from '@/router/constants';
import VueMeta from 'vue-meta';
import VueZoomer from 'vue-zoomer';
import { firestorePlugin } from './vuefire/src';
import iframeResize from 'iframe-resizer/js/iframeResizer';
import VuetifyTooltipOverride from './plugins/VuetifyTooltipOverride';
import VueDOMPurifyHTML from 'vue-dompurify-html'
import posthog from 'posthog-js';
import { initAnalytics } from '@/services/analytics';

// PostHog project token — a publishable, write-only client key ("Safe to use in
// public apps", per PostHog's own UI). It can only send events; it can't read or
// administer anything, so it's safe to commit and ship in the browser bundle.
// Kept as a code constant on purpose: the production build is self-contained and
// never depends on a (gitignored) .env file being present in CI. An env var still
// wins if set — e.g. to point a local build at a different PostHog project.
const POSTHOG_PROJECT_TOKEN = 'phc_AtWWcsjKEGY5k36fPUdWMx53NrE4XKxhe9rG8jMKwa5U';
const posthogToken = process.env.VUE_APP_POSTHOG_PROJECT_TOKEN || POSTHOG_PROJECT_TOKEN;

// Autocapture (auto-tracked clicks/pageviews + rage/dead-click frustration
// signals) is OFF by default to protect the free-tier event quota. Flip it on
// without a code change by setting VUE_APP_POSTHOG_AUTOCAPTURE=true; set it back
// to anything else (or remove it) to turn it off again. Manual analytics.track()
// events are unaffected by this flag.
const autocaptureEnabled = process.env.VUE_APP_POSTHOG_AUTOCAPTURE === 'true';

if (posthogToken) {
	posthog.init(posthogToken, {
		api_host: process.env.VUE_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
		person_profiles: 'identified_only',
		autocapture: autocaptureEnabled,
		capture_pageview: autocaptureEnabled,
		capture_pageleave: autocaptureEnabled,
		// Web-vitals ($web_vitals) capture is a SEPARATE switch that posthog-js
		// defaults to ON — `autocapture: false` does not disable it. Left at its
		// default it was ingesting ~192k events/mo (24% of all project volume)
		// that nothing reads: performance monitoring is not what this project is
		// for. Turn it on deliberately if that ever changes.
		capture_performance: false,
		disable_session_recording: true, // session replay is handled by Microsoft Clarity
	});
	(window as any).posthog = posthog;
}

initAnalytics(!!posthogToken);

Vue.config.productionTip = false;
Vue.config.ignoredElements = ['img-comparison-slider'];

Vue.use(VueMeta, {
	// optional pluginOptions
	keyName: 'metaInfo',
	refreshOnceOnNavigation: true,
});
Vue.use(AutoSoundMachine);
Vue.use(firestorePlugin);
Vue.use(VueMoment);
Vue.use(VueZoomer);
Vue.use(VuetifyTooltipOverride, { vuetify });
Vue.use(VueScrollTo, {
	container: 'body',
	duration: 500,
	easing: 'ease',
	offset: -170,
	cancelable: true,
	onDone: false,
	onBack: false,
});
Vue.use(VueDOMPurifyHTML);

Vue.directive('resize', {
	bind: function (el, { value = {} }) {
		el.addEventListener('load', () => iframeResize(value, el))
	},
	unbind: function (el) {
		const iFrameResizer = _get(el, 'iFrameResizer');

		if (iFrameResizer) {
			iFrameResizer.removeListeners();
		}
	}
});

Vue.component('BaseButton', BaseButton);

// Global Mixin
Vue.mixin({
	methods: {
		_get,
		consoleLog: (...params) => console.log(...params),
		consoleError: (...params) => console.error(...params),
		routerPush: (dollarRoute: Route, dollarRouter: VueRouter, location: RawLocation): Promise<Route> => {
			const windowFullPath: string = window.location.pathname + window.location.search;
			const nextFullPath: string = dollarRouter.resolve(location).href;
			if (windowFullPath !== nextFullPath) {
				if (dollarRoute.name === ROUTER.RUNNIT_REROUTE) {
					// If we're currently on a reroute page, always use replace instead of push
					// This prevents the reroute page from staying in history
					return dollarRouter.replace(location) as Promise<Route>;
				}
				return dollarRouter.push(location) as Promise<Route>;
			}
			return Promise.resolve(dollarRoute);
		},
		routerReplace: (dollarRoute: Route, dollarRouter: VueRouter, location: RawLocation): Promise<Route> => {
			const windowFullPath: string = window.location.pathname + window.location.search;
			const nextFullPath: string = dollarRouter.resolve(location).href;
			if (windowFullPath !== nextFullPath) {
				return dollarRouter.replace(location) as Promise<Route>;
			}
			return Promise.resolve(dollarRoute);
		},
		routerNewWindow: (dollarRouter: VueRouter, location: RawLocation): void => {
			const resolvedRoute = dollarRouter.resolve(location);
			window.open(resolvedRoute.href, '_blank');
		},
	},
});


new Vue({
	router,
	store,
	vuetify,
	render: h => h(App),
}).$mount('#app');
